import React, { forwardRef, useState, useEffect } from 'react'

import { ChevronDownIconSmall } from '@hm-group/fabric-icons/icons/ChevronDownIcon'
import { ChevronUpIconSmall } from '@hm-group/fabric-icons/icons/ChevronUpIcon'
import { SearchIconSmall } from '@hm-group/fabric-icons/icons/SearchIcon'
import cn from 'classnames'

import IconButton from 'components/Buttons/IconButton'
import { DropDown } from 'components/DropDown'
import BaseInput from 'components/Fields/BaseInput'
import { BaseInputLabel } from 'components/Fields/BaseInput/components/BaseInputLabel'
import { BaseInputMetaText } from 'components/Fields/BaseInput/components/BaseInputMetaText'

import styles from './Select.module.scss'
import { SelectProps } from './types'

const Select: React.FC<SelectProps> = forwardRef(
  (
    {
      children,
      ariaLabel,
      onSelected,
      onChange,
      isRequired,
      isInvalid,
      isDisabled,
      isReadOnly = false,
      helperText,
      placeholder,
      label,
      name,
      className,
      placement = 'bottom-start',
      iconLabel = 'open',
      value,
      isPhoneNumber,
      inputProps,
      isLabelVisible = true,
      isSearch = false,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState<string>('')

    useEffect(() => {
      setInputValue(value)
    }, [value])

    const handleOnSelect = (event): void => {
      setInputValue(isPhoneNumber ? event.metaText : event.label)
      onSelected(event)
    }

    const handleOnChange = (event): void => {
      setInputValue(event.currentTarget.value)
      if (isSearch) {
        setIsOpen(event.currentTarget.value.length > 2)
      } else {
        setIsOpen(true)
      }
      onChange?.(event)
    }

    const onClose = (): void => {
      setIsOpen(false)
    }

    const onOpen = (): void => {
      setIsOpen(true)
    }

    return (
      <div>
        <BaseInputLabel
          name={name}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isLabelVisible={isLabelVisible}
        >
          {label}
        </BaseInputLabel>
        <DropDown
          {...props}
          ariaLabel={ariaLabel}
          onClose={onClose}
          onOpen={onOpen}
          onSelected={handleOnSelect}
          isContentVisible={isOpen}
          className={cn(styles.select, className)}
          isSelect
          placement={placement}
          id={name}
          isControlled={isSearch}
          isControlledOpened={isOpen}
          ref={ref}
          triggerElement={
            <BaseInput
              {...inputProps}
              label={label}
              isLabelVisible={false}
              inputClassName={cn(inputProps?.inputClassName, !isDisabled && styles.input)}
              isRequired={isRequired}
              name={name}
              value={inputValue}
              placeholder={placeholder}
              isInvalid={isInvalid}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              onChange={handleOnChange}
              type="text"
              afterContentPadding={false}
              afterContent={
                <IconButton
                  tabIndex={-1}
                  variant="ghost"
                  iconElement={
                    isSearch ? (
                      <SearchIconSmall />
                    ) : isOpen ? (
                      <ChevronUpIconSmall />
                    ) : (
                      <ChevronDownIconSmall />
                    )
                  }
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {iconLabel}
                </IconButton>
              }
            />
          }
        >
          {children}
        </DropDown>
        {helperText && <BaseInputMetaText isInvalid={isInvalid}>{helperText}</BaseInputMetaText>}
      </div>
    )
  },
)

export default Select
